import { useEffect, useState, useCallback, useRef } from "react";
import API from "../../services/api";
import {
  Row,
  Col,
  Spin,
  Button,
  Empty,
  Space,
  Pagination,
  Modal,
  AutoComplete,
  Select,
} from "antd";
import {
  FilterOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  SearchOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./Franchises.css";
import { filterCities } from "../locationService";
import {
  validateLocality,
  validateNumericRange,
  validateCustomText,
  isEmpty,
} from "../../utils/Filtervalidation.js";
import { useFilterValidation } from "../../hooks/Usefiltervalidation.js";
// ─────────────────────────────────────────────────────────────────────────────
// NEW: reusable location hook
// ─────────────────────────────────────────────────────────────────────────────
import { useLocationSearch } from "../../hooks/Uselocationsearch.js";

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED VALUES
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_CATEGORIES = [
  "food", "retail", "education", "fitness", "healthcare", "beauty", "technology", "other",
];
const ALLOWED_SORT_BY = ["investment_low", "investment_high", "outlet_high"];
const MAX_INVESTMENT = 10_000_000_000;
const MAX_AREA = 10_000_000;
const MAX_OUTLET = 1_000_000;
const MAX_FEE = 10_000_000_000;
const MAX_ROYALTY = 100;

// ─────────────────────────────────────────────────────────────────────────────
// ValidationTooltip
// ─────────────────────────────────────────────────────────────────────────────
function ValidationTooltip({ message }) {
  if (!message) return null;
  return (
    <div className="vt-tooltip" role="alert" aria-live="polite">
      <span className="vt-tooltip-icon" aria-hidden="true" />
      <span className="vt-tooltip-text">{message}</span>
    </div>
  );
}

function NumericRangePair({
  label,
  minKey,
  maxKey,
  maxAllowed,
  errorKey,
  fieldRef,
  filters,
  setFilters,
  fieldErrors,
  sanitizeNumericInput,
}) {
  return (
    <div className="fn-filter-group vt-field-wrap" ref={fieldRef}>
      <label className="fn-filter-label">{label}</label>

      <div className="fn-filter-row">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Min"
          value={filters[minKey] || ""}
          className={`fn-filter-input-half${fieldErrors[errorKey] ? " vt-input-error" : ""
            }`}
          onChange={(e) => {
            const val = sanitizeNumericInput(
              e.target.value,
              maxAllowed
            );

            setFilters((prev) => ({
              ...prev,
              [minKey]: val,
            }));
          }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />

        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Max"
          value={filters[maxKey] || ""}
          className={`fn-filter-input-half${fieldErrors[errorKey] ? " vt-input-error" : ""
            }`}
          onChange={(e) => {
            const val = sanitizeNumericInput(
              e.target.value,
              maxAllowed
            );

            setFilters((prev) => ({
              ...prev,
              [maxKey]: val,
            }));
          }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
      </div>

      <ValidationTooltip message={fieldErrors[errorKey]} />
    </div>
  );
}

function Franchises() {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const CATEGORY_OPTIONS = [
    { value: "food", label: "Food" },
    { value: "retail", label: "Retail" },
    { value: "education", label: "Education" },
    { value: "fitness", label: "Fitness" },
    { value: "healthcare", label: "Healthcare" },
    { value: "beauty", label: "Beauty" },
    { value: "technology", label: "Technology" },
    { value: "other", label: "Other" },
  ];

  const SORT_OPTIONS = [
    {
      value: "investment_low",
      label: "Investment Low → High",
    },
    {
      value: "investment_high",
      label: "Investment High → Low",
    },
    {
      value: "outlet_high",
      label: "Most Outlets",
    },
  ];

  // ── Search-bar suggestion state (driven by useLocationSearch hook)
  const [suggestions, setSuggestions] = useState([]);

  // ── Filter-modal locality suggestion state (separate, unchanged)
  const [filterSuggestions, setFilterSuggestions] = useState([]);

  const [customCategoryFilter, setCustomCategoryFilter] = useState("");

  // ── Validation hook ────────────────────────────────────────────────────────
  const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError } =
    useFilterValidation();

  // ── Refs for scroll-to-error ───────────────────────────────────────────────
  const localityRef = useRef(null);
  const investmentRef = useRef(null);
  const areaRef = useRef(null);
  const outletRef = useRef(null);
  const feeRef = useRef(null);
  const royaltyRef = useRef(null);
  const customCategoryRef = useRef(null);

  const [modalWidth, setModalWidth] = useState(600);

  // filterDebounceRef is still used by the filter-modal locality field
  const filterDebounceRef = useRef(null);
  const filterContentRef = useRef(null);

  const searchDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 576) {
        setModalWidth("96vw");
      } else if (window.innerWidth <= 768) {
        setModalWidth("95vw");
      } else {
        setModalWidth(600);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsAdmin(user?.role === "admin");
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    locality: "",
    city: "",
    category: "",
    minInvestment: "",
    maxInvestment: "",
    minArea: "",
    maxArea: "",
    minOutlet: "",
    maxOutlet: "",
    minFee: "",
    maxFee: "",
    minRoyalty: "",
    maxRoyalty: "",
    sortBy: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // ── NEW: reusable location-search hook for the main search bar ─────────────
  const {
    inputValue: searchInputValue,
    isLocationValid: isSearchLocationValid,
    handleChange: handleSearchChange,
    handleSelect: handleSearchSelect,
    handleKeyDown: makeSearchKeyDown,
    handleFocus: handleSearchFocus,
    resetLocation: resetSearchLocation,
  } = useLocationSearch({
    onSuggestionsChange: setSuggestions,
    debounceMs: 300,
    minChars: 2,

    // SYNC FILTER → SEARCH
    syncedLocality: filters.locality,

    // SYNC SEARCH → FILTER
    onSyncLocality: (value) => {

      setFilters((prev) => {

        if (prev.locality === value) {
          return prev;
        }

        return {
          ...prev,
          locality: value,
        };
      });
    },
  });

  // ── Sanitize numeric input ────────────────────────────────────────────────
  const sanitizeNumericInput = (value, max) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }

    // ONLY positive integers allowed
    const stripped = String(value).replace(/[^0-9]/g, "");

    if (stripped === "") {
      return "";
    }

    const n = Number(stripped);

    if (!Number.isInteger(n) || n < 0) {
      return "";
    }

    if (max !== undefined && n > max) {
      return String(max);
    }

    return stripped;
  };

  // ── Build clean params ────────────────────────────────────────────────────
  const buildParams = useCallback((customFilters, page, pageSize) => {
    const params = { page, limit: pageSize };

    if (customFilters.search?.trim()) {
      params.search = customFilters.search.trim().slice(0, 200);
    }
    if (customFilters.locality?.trim()) {
      params.locality = customFilters.locality.trim().slice(0, 100);
    }
    if (customFilters.category?.trim()) {
      params.category = customFilters.category.trim().slice(0, 100);
    }

    const numericFields = [
      ["minInvestment", MAX_INVESTMENT],
      ["maxInvestment", MAX_INVESTMENT],
      ["minArea", MAX_AREA],
      ["maxArea", MAX_AREA],
      ["minOutlet", MAX_OUTLET],
      ["maxOutlet", MAX_OUTLET],
      ["minFee", MAX_FEE],
      ["maxFee", MAX_FEE],
      ["minRoyalty", MAX_ROYALTY],
      ["maxRoyalty", MAX_ROYALTY],
    ];

    for (const [key, max] of numericFields) {
      const n = Number(customFilters[key]);
      if (customFilters[key] !== "" && isFinite(n) && n >= 0 && n <= max) {
        params[key] = n;
      }
    }

    if (customFilters.sortBy && ALLOWED_SORT_BY.includes(customFilters.sortBy)) {
      params.sortBy = customFilters.sortBy;
    }

    return params;
  }, []);

  const fetchFranchises = useCallback(
    async (customFilters = filters, page = 1, pageSize = 15) => {
      try {
        setLoading(true);
        const params = buildParams(customFilters, page, pageSize);
        const res = await API.get("/franchises", { params });
        setFranchises(res.data.franchises || []);
        setPagination({
          current: res.data.currentPage || 1,
          pageSize,
          total: res.data.total || 0,
        });
      } catch (err) {
        console.log("Fetch error:", err);
        setFranchises([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchFranchises(filters, 1, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run per-field validation, collect errors, scroll to first ────────────
  const runFieldValidations = (filtersToValidate, customCat) => {
    const errors = {};

    const localityResult = validateLocality(filtersToValidate.locality);
    if (!localityResult.valid) errors.locality = localityResult.message;

    const investmentResult = validateNumericRange(
      filtersToValidate.minInvestment,
      filtersToValidate.maxInvestment,
      "Investment"
    );
    if (!investmentResult.valid) errors.investment = investmentResult.message;

    const areaResult = validateNumericRange(
      filtersToValidate.minArea,
      filtersToValidate.maxArea,
      "Area"
    );
    if (!areaResult.valid) errors.area = areaResult.message;

    const outletResult = validateNumericRange(
      filtersToValidate.minOutlet,
      filtersToValidate.maxOutlet,
      "Outlet Count"
    );
    if (!outletResult.valid) errors.outlet = outletResult.message;

    const feeResult = validateNumericRange(
      filtersToValidate.minFee,
      filtersToValidate.maxFee,
      "Franchise Fee"
    );
    if (!feeResult.valid) errors.fee = feeResult.message;

    const royaltyResult = validateNumericRange(
      filtersToValidate.minRoyalty,
      filtersToValidate.maxRoyalty,
      "Royalty Fee"
    );
    if (!royaltyResult.valid) errors.royalty = royaltyResult.message;

    if (filtersToValidate.category === "other") {
      const customCatResult = validateCustomText(customCat, "Custom Category");
      if (!customCatResult.valid) errors.customCategory = customCatResult.message;
    }

    setFieldErrors(errors);

    const refMap = {
      locality: localityRef,
      investment: investmentRef,
      area: areaRef,
      outlet: outletRef,
      fee: feeRef,
      royalty: royaltyRef,
      customCategory: customCategoryRef,
    };
    scrollToFirstError(refMap, errors);

    return Object.keys(errors).length === 0;
  };

  // ── REAL‑TIME ERROR CLEARING ─────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;

    const newErrors = { ...fieldErrors };

    // Locality
    if (newErrors.locality) {
      const localityResult = validateLocality(filters.locality);
      if (localityResult.valid) delete newErrors.locality;
    }

    // Investment
    if (newErrors.investment) {
      const result = validateNumericRange(filters.minInvestment, filters.maxInvestment, "Investment");
      if (result.valid) delete newErrors.investment;
    }

    // Area
    if (newErrors.area) {
      const result = validateNumericRange(filters.minArea, filters.maxArea, "Area");
      if (result.valid) delete newErrors.area;
    }

    // Outlet
    if (newErrors.outlet) {
      const result = validateNumericRange(filters.minOutlet, filters.maxOutlet, "Outlet Count");
      if (result.valid) delete newErrors.outlet;
    }

    // Fee
    if (newErrors.fee) {
      const result = validateNumericRange(filters.minFee, filters.maxFee, "Franchise Fee");
      if (result.valid) delete newErrors.fee;
    }

    // Royalty
    if (newErrors.royalty) {
      const result = validateNumericRange(filters.minRoyalty, filters.maxRoyalty, "Royalty Fee");
      if (result.valid) delete newErrors.royalty;
    }

    // Custom Category
    if (newErrors.customCategory) {
      const customResult = validateCustomText(customCategoryFilter, "Custom Category");
      if (customResult.valid) delete newErrors.customCategory;
    }

    if (Object.keys(newErrors).length !== Object.keys(fieldErrors).length) {
      setFieldErrors(newErrors);
    }
  }, [
    filters.locality,
    filters.minInvestment,
    filters.maxInvestment,
    filters.minArea,
    filters.maxArea,
    filters.minOutlet,
    filters.maxOutlet,
    filters.minFee,
    filters.maxFee,
    filters.minRoyalty,
    filters.maxRoyalty,
    customCategoryFilter,
    fieldErrors,
    setFieldErrors,
  ]);


  useEffect(() => {
    const handleOutsideClick = (e) => {

      // SEARCH DROPDOWN
      const clickedSearchArea = e.target.closest(
        ".fn-search-field"
      );

      if (!clickedSearchArea) {
        setSuggestions([]);
      }

      // FILTER LOCALITY DROPDOWN
      const clickedFilterArea = e.target.closest(
        ".fn-filter-group"
      );

      if (!clickedFilterArea) {
        setFilterSuggestions([]);
      }

      // CUSTOM DROPDOWNS
      if (!e.target.closest(".fn-custom-dropdown")) {

        setShowCategoryDropdown(false);

        setShowSortDropdown(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    if (filterModalVisible) {
      setTimeout(() => {
        const modalBody = document.querySelector(
          ".fn-filter-modal .ant-modal-body"
        );

        modalBody?.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 50);
    }
  }, [filterModalVisible]);

  // ── handleApplyFilters (filter modal) ────────────────────────────────────
  const handleApplyFilters = async () => {
    const isValid = runFieldValidations(filters, customCategoryFilter);
    if (!isValid) return;

    let updatedFilters = { ...filters, search: "" };

    if (updatedFilters.category === "other") {
      const trimmedCustom = customCategoryFilter.trim();
      updatedFilters.category = trimmedCustom ? trimmedCustom.slice(0, 100) : "";
    }

    setFilters(updatedFilters);
    setSuggestions([]);
    setFilterSuggestions([]);
    clearAllFieldErrors();
    fetchFranchises(updatedFilters, 1, pagination.pageSize);
    setFilterModalVisible(false);
  };

  // ── handleSearchBarApply (main search bar) ────────────────────────────────
  // Called ONLY when the Search button is clicked or Enter pressed (valid).
  const handleSearchBarApply = async () => {

    const selectedValue = searchInputValue.trim();

    const isLocationSearch =
      suggestions.some(
        (item) =>
          item.value.toLowerCase() ===
          selectedValue.toLowerCase()
      );

    const updatedFilters = {
      search: isLocationSearch
        ? ""
        : selectedValue,

      locality: isLocationSearch
        ? selectedValue
        : "",

      city: "",
      category: "",
      minInvestment: "",
      maxInvestment: "",
      minArea: "",
      maxArea: "",
      minOutlet: "",
      maxOutlet: "",
      minFee: "",
      maxFee: "",
      minRoyalty: "",
      maxRoyalty: "",
      sortBy: "",
    };

    setFilters(updatedFilters);

    setSuggestions([]);

    clearAllFieldErrors();

    fetchFranchises(
      updatedFilters,
      1,
      pagination.pageSize
    );
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      locality: "",
      city: "",
      category: "",
      minInvestment: "",
      maxInvestment: "",
      minArea: "",
      maxArea: "",
      minOutlet: "",
      maxOutlet: "",
      minFee: "",
      maxFee: "",
      minRoyalty: "",
      maxRoyalty: "",
      sortBy: "",
    };
    setFilters(resetFilters);
    setCustomCategoryFilter("");
    setFilterSuggestions([]);
    resetSearchLocation();
    clearAllFieldErrors();
    fetchFranchises(resetFilters, 1, pagination.pageSize);

    const modalBody = document.querySelector(
      ".fn-filter-modal .ant-modal-body"
    );

    modalBody?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePageChange = (page) => {
    fetchFranchises(filters, page, 15);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatInvestment = (investment) => {
    if (!investment && investment !== 0)
      return { amount: "Investment on request", label: "" };
    return {
      amount: `₹${Number(investment).toLocaleString("en-IN")}`,
      label: "investment",
    };
  };

  const activeFiltersCount = [
    filters.locality,
    filters.category === "other" ? customCategoryFilter : filters.category,
    filters.minInvestment,
    filters.maxInvestment,
    filters.minArea,
    filters.maxArea,
    filters.minOutlet,
    filters.maxOutlet,
    filters.minFee,
    filters.maxFee,
    filters.minRoyalty,
    filters.maxRoyalty,
    filters.sortBy,
  ].filter(Boolean).length;

  // ── Reusable numeric range pair renderer ─────────────────────────────────
  const renderFilterDrawer = () => (
    <div className="fn-filter-panel">
      <Space orientation="vertical" size={14} style={{ width: "100%" }}>

        {/* Locality */}
        <div className="fn-filter-group vt-field-wrap" ref={localityRef}>
          <label className="fn-filter-label">Locality</label>
          <div
            className="fn-search-custom"
            ref={searchDropdownRef}
          >
            <input
              type="text"
              className={`fn-filter-input ${fieldErrors.locality ? "vt-input-error" : ""
                }`}
              placeholder="Search locality"
              maxLength={100}
              value={filters.locality}
              onFocus={async () => {
                if (filters.locality.trim().length >= 2) {
                  const results = await filterCities(
                    filters.locality
                  );

                  setFilterSuggestions(results || []);
                }
              }}
              onChange={(e) => {
                const value = e.target.value;

                setFilters((prev) => ({
                  ...prev,
                  locality: value,
                }));

                clearTimeout(filterDebounceRef.current);

                if (!value || value.length < 2) {
                  setFilterSuggestions([]);
                  return;
                }

                filterDebounceRef.current = setTimeout(
                  async () => {
                    try {
                      const results = await filterCities(value);

                      setFilterSuggestions(results || []);
                    } catch (err) {
                      console.error(err);
                    }
                  },
                  300
                );
              }}
            />

            {filterSuggestions.length > 0 && (
              <div className="fn-search-dropdown-custom">
                {filterSuggestions.map((item, index) => (
                  <div
                    key={index}
                    className="fn-search-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        locality: item.value,
                      }));

                      setFilterSuggestions([]);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ValidationTooltip message={fieldErrors.locality} />
        </div>

        {/* Category */}
        <div className="fn-filter-group">
          <label className="fn-filter-label">Category</label>
          <div className="fn-custom-dropdown">
            <div
              className={`fn-custom-dropdown-input ${showCategoryDropdown ? "active" : ""
                }`}
              onClick={() =>
                setShowCategoryDropdown(
                  !showCategoryDropdown
                )
              }
            >
              {
                CATEGORY_OPTIONS.find(
                  (item) => item.value === filters.category
                )?.label || "Select category"
              }

              <span
                className={`fn-dropdown-arrow ${showCategoryDropdown ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {showCategoryDropdown && (
              <div className="fn-custom-dropdown-menu">
                {CATEGORY_OPTIONS.map((item, index) => (
                  <div
                    key={index}
                    className={`fn-custom-dropdown-item ${filters.category === item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        category: item.value,
                      }));

                      setShowCategoryDropdown(false);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {filters.category === "other" && (
          <div
            className="fn-filter-group vt-field-wrap"
            style={{ marginTop: 8 }}
            ref={customCategoryRef}
          >
            <label className="fn-filter-label">Custom Category</label>
            <input
              type="text"
              placeholder="e.g. Food, Retail"
              value={customCategoryFilter}
              maxLength={100}
              onChange={(e) => setCustomCategoryFilter(e.target.value)}
              className={`fn-filter-input${fieldErrors.customCategory ? " vt-input-error" : ""}`}
            />
            <ValidationTooltip message={fieldErrors.customCategory} />
          </div>
        )}

        {/* Investment Range */}
        <NumericRangePair
          label="Investment Range (₹)"
          minKey="minInvestment"
          maxKey="maxInvestment"
          maxAllowed={MAX_INVESTMENT}
          errorKey="investment"
          fieldRef={investmentRef}
          filters={filters}
          setFilters={setFilters}
          fieldErrors={fieldErrors}
          sanitizeNumericInput={sanitizeNumericInput}
        />

        {/* Area Required */}
        <NumericRangePair
          label="Area Required (sqft)"
          minKey="minArea"
          maxKey="maxArea"
          maxAllowed={MAX_AREA}
          errorKey="area"
          fieldRef={areaRef}
          filters={filters}
          setFilters={setFilters}
          fieldErrors={fieldErrors}
          sanitizeNumericInput={sanitizeNumericInput}
        />

        {/* Outlet Count */}
        <NumericRangePair
          label="Outlet Count"
          minKey="minOutlet"
          maxKey="maxOutlet"
          maxAllowed={MAX_OUTLET}
          errorKey="outlet"
          fieldRef={outletRef}
          filters={filters}
          setFilters={setFilters}
          fieldErrors={fieldErrors}
          sanitizeNumericInput={sanitizeNumericInput}
        />

        {/* Franchise Fee */}
        <NumericRangePair
          label="Franchise Fee (₹)"
          minKey="minFee"
          maxKey="maxFee"
          maxAllowed={MAX_FEE}
          errorKey="fee"
          fieldRef={feeRef}
          filters={filters}
          setFilters={setFilters}
          fieldErrors={fieldErrors}
          sanitizeNumericInput={sanitizeNumericInput}
        />

        {/* Royalty Fee */}
        <NumericRangePair
          label="Royalty Fee (%)"
          minKey="minRoyalty"
          maxKey="maxRoyalty"
          maxAllowed={MAX_ROYALTY}
          errorKey="royalty"
          fieldRef={royaltyRef}
          filters={filters}
          setFilters={setFilters}
          fieldErrors={fieldErrors}
          sanitizeNumericInput={sanitizeNumericInput}
        />

        {/* Sort By */}
        <div className="fn-filter-group">
          <label className="fn-filter-label">Sort By</label>
          <div className="fn-custom-dropdown">
            <div
              className={`fn-custom-dropdown-input ${showSortDropdown ? "active" : ""
                }`}
              onClick={() =>
                setShowSortDropdown(
                  !showSortDropdown
                )
              }
            >
              {
                SORT_OPTIONS.find(
                  (item) => item.value === filters.sortBy
                )?.label || "Sort by"
              }

              <span
                className={`fn-dropdown-arrow ${showSortDropdown ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {showSortDropdown && (
              <div className="fn-custom-dropdown-menu">
                {SORT_OPTIONS.map((item, index) => (
                  <div
                    key={index}
                    className={`fn-custom-dropdown-item ${filters.sortBy === item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        sortBy: item.value,
                      }));

                      setShowSortDropdown(false);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="fn-filter-actions">
          <Button block onClick={handleApplyFilters} className="fn-apply-btn">
            Apply Filters
          </Button>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={handleReset}
            className="fn-reset-btn"
          >
            Reset All
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <div className="fn-page">
      <div className="fn-container">
        <header className="fn-hero">
          <div className="fn-hero-content">
            <span className="fn-hero-tag">Real Estate Marketplace</span>
            <h1 className="fn-hero-title">Franchises</h1>
            <p className="fn-hero-desc">
              Discover investment opportunities with leading brands across industries
            </p>
          </div>
          <div className="fn-hero-decoration" aria-hidden="true">
            <div className="fn-hero-circle fn-hero-circle--lg" />
            <div className="fn-hero-circle fn-hero-circle--sm" />
          </div>
        </header>

        <section className="fn-bar">
          <div className="fn-bar-top">
            <div className="fn-bar-count">
              <strong>{pagination.total.toLocaleString()}</strong> franchises available
            </div>
            {activeFiltersCount > 0 && (
              <button className="fn-clear-all" onClick={handleReset}>
                Clear all ({activeFiltersCount})
              </button>
            )}
          </div>

          <div className="fn-bar-controls">
            <div className="fn-search-field">
              <SearchOutlined className="fn-search-ico" />

              {/*
               * UPGRADED AutoComplete
               *
               * - onChange  → handleSearchChange (marks invalid, fetches suggestions)
               * - onSelect  → handleSearchSelect (marks valid, NO auto-fetch)
               * - value     → searchInputValue (controlled by hook)
               * - inner input onKeyDown → only fires handleSearchBarApply when valid
               */}
              <div
                className="fn-search-custom"
                ref={filterDropdownRef}
              >
                <input
                  type="text"
                  className="fn-search-input"
                  placeholder="Search by location, brand name, or category..."
                  maxLength={200}
                  value={searchInputValue}
                  onFocus={handleSearchFocus}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleSearchChange(value);

                    setFilters((prev) => ({
                      ...prev,
                      search: value.slice(0, 200),
                      locality: "",
                      city: "",
                      category: "",
                      minInvestment: "",
                      maxInvestment: "",
                      minArea: "",
                      maxArea: "",
                      minOutlet: "",
                      maxOutlet: "",
                      minFee: "",
                      maxFee: "",
                      minRoyalty: "",
                      maxRoyalty: "",
                      sortBy: "",
                    }));

                    clearAllFieldErrors();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchBarApply();
                    }
                  }}
                />

                {suggestions.length > 0 && (
                  <div className="fn-search-dropdown-custom">
                    {suggestions.map((item, index) => (
                      <div
                        key={index}
                        className="fn-search-dropdown-item"
                        onClick={() => {
                          handleSearchSelect(item.value);
                          setSuggestions([]);
                        }}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/*
             * UPGRADED Search button
             *
             * - disabled when no valid dropdown location has been selected
             */}
            <button
              className="fn-search-btn"
              onClick={handleSearchBarApply}

            >
              Search
            </button>

            <button
              className="fn-filter-btn"
              onClick={() => setFilterModalVisible(true)}
            >
              <FilterOutlined />
              <span className="fn-filter-btn-label">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="fn-filter-badge">{activeFiltersCount}</span>
              )}
            </button>
          </div>
        </section>

        <Modal
          title="Filter Franchises"
          open={filterModalVisible}
          onCancel={() => {
            setFilterModalVisible(false);
            clearAllFieldErrors();
          }}
          footer={null}
          centered
          width={modalWidth}
          className="fn-filter-modal"
          mask={{ closable: true }}
          styles={{
            body: {
              overflowY: "auto",
            },
          }}
        >
          {renderFilterDrawer()}
        </Modal>

        {loading ? (
          <div className="fn-state-wrap">
            <Spin size="large" />
            <p className="fn-state-msg">Loading franchises…</p>
          </div>
        ) : franchises.length === 0 ? (
          <div className="fn-state-wrap">
            <Empty description={<span className="fn-state-msg">No franchises found</span>} />
          </div>
        ) : (
          <>
            <Row gutter={[20, 20]} className="fn-grid">
              {franchises.map((item) => {
                const investment = formatInvestment(
                  item.franchiseDetails?.investmentRequired
                );
                const approvalStatus = item.approvalStatus || "pending";
                const activeStatus = item.status || "inactive";

                return (
                  <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6} key={item._id}>
                    <article className="fn-card">
                      <div className="fn-card-thumb">
                        <img
                          alt={item.franchiseDetails?.brandName}
                          src={
                            item.thumbnail ||
                            item.images?.[0] ||
                            "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"
                          }
                          className="fn-card-img"
                          loading="lazy"
                        />
                        <div className="fn-card-overlay" />
                        <div className="fn-card-tags">
                          {item.franchiseDetails?.category && (
                            <span className="fn-tag fn-tag--category">
                              {item.franchiseDetails.category}
                            </span>
                          )}
                          {item.isFeatured && (
                            <span className="fn-tag fn-tag--featured">Featured</span>
                          )}
                        </div>
                      </div>
                      <div className="fn-card-body">
                        <div className="fn-card-location">
                          <EnvironmentOutlined className="fn-loc-ico" />
                          <span className="fn-loc-text">
                            {[
                              item.headquarters?.locality,
                              item.headquarters?.city,
                              item.headquarters?.state,
                            ]
                              .filter(Boolean)
                              .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                              .join(", ")}
                          </span>
                        </div>
                        <div className="fn-brand-row">
                          <div className="fn-brand-icon">
                            <BankOutlined style={{ fontSize: "13px" }} />
                          </div>
                          <h3 className="fn-card-name">
                            {item.franchiseDetails?.brandName || item.title}
                          </h3>
                        </div>
                        <div className="fn-card-price">
                          <span className="fn-price-num">{investment.amount}</span>
                          {investment.label && (
                            <span className="fn-price-unit">{investment.label}</span>
                          )}
                        </div>
                        <div className="fn-card-specs">
                          {item.franchiseDetails?.outletCount && (
                            <div className="fn-spec-item">
                              <span className="fn-spec-ico">⊞</span>
                              <span>{item.franchiseDetails.outletCount}+ outlets</span>
                            </div>
                          )}
                          {item.franchiseDetails?.areaRequired && (
                            <div className="fn-spec-item">
                              <span className="fn-spec-ico">▪</span>
                              <span>
                                {item.franchiseDetails.areaRequired} sqft required
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="fn-card-footer">
                          {isAdmin && (
                            <div className="fn-card-statuses">
                              <span
                                className={`fn-pill fn-pill--${activeStatus === "active" ? "active" : "inactive"
                                  }`}
                              >
                                {activeStatus}
                              </span>
                              <span className={`fn-pill fn-pill--${approvalStatus}`}>
                                {approvalStatus}
                              </span>
                            </div>
                          )}
                          <Link
                            to={`/franchise/${item.slug}`}
                            className="fn-card-cta"
                          >
                            View details →
                          </Link>
                        </div>
                      </div>
                    </article>
                  </Col>
                );
              })}
            </Row>
            <div className="fn-pagination">
              <Pagination
                current={pagination.current}
                pageSize={15}
                total={pagination.total}
                onChange={handlePageChange}
                responsive
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Franchises;